import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { AlmanacLocation, Frame } from '@/types/location';

interface Context {
  locations: AlmanacLocation[];
  setLocations: Dispatch<SetStateAction<AlmanacLocation[]>>;
  activeLocation: AlmanacLocation | undefined;
  setActiveLocation: Dispatch<SetStateAction<AlmanacLocation | undefined>>;
  sortedSequences: Frame[][];
  setSortedSequences: Dispatch<SetStateAction<Frame[][]>>;
  activeSequenceIndex: number;
  setActiveSequenceIndex: Dispatch<SetStateAction<number>>;
  activeFrameIndex: number;
  setActiveFrameIndex: Dispatch<SetStateAction<number>>;
  reset: () => void;
}

const AlmanacContext = createContext<Context | null>(null);

export const AlmanacProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [locations, setLocations] = useState<AlmanacLocation[]>([]);
  const [activeLocation, setActiveLocation] = useState<AlmanacLocation>();
  const [sortedSequences, setSortedSequences] = useState<Frame[][]>([[]]);
  const [activeSequenceIndex, setActiveSequenceIndex] = useState<number>(0);
  const [activeFrameIndex, setActiveFrameIndex] = useState<number>(0);
  const reset = useCallback(() => {
    setActiveFrameIndex(0);
    setActiveSequenceIndex(0);
    setSortedSequences([[]]);
  }, [setActiveSequenceIndex, setActiveFrameIndex, setSortedSequences]);

  return (
    <AlmanacContext.Provider
      value={{
        locations,
        setLocations,
        activeLocation,
        setActiveLocation,
        sortedSequences,
        setSortedSequences,
        activeSequenceIndex,
        setActiveSequenceIndex,
        activeFrameIndex,
        setActiveFrameIndex,
        reset,
      }}>
      {children}
    </AlmanacContext.Provider>
  );
};

export const useAlmanac = () => {
  const context = useContext(AlmanacContext);
  if (!context) {
    throw new Error('useAlmanac must be used within the AlmanacProvider');
  }

  return context;
};
